/**
 * FloatingAskBosco — Task D
 * Floating, minimizable "Ask Bosco" widget pinned to the bottom-right corner.
 * - Default state: minimized (just a FAB button)
 * - Open: pop-out panel with the existing TeachingCoPilot chat
 * - Outside click does NOT close — state preserved until user explicitly
 *   minimizes via the chevron/X.
 * - Persists open/minimized preference in localStorage across navigations.
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Minus, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TeachingCoPilot } from './TeachingCoPilot';

const STORAGE_KEY = 'askBosco.open.v1';

export function FloatingAskBosco({ access, context }) {
  // Default = minimized (per spec).
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, open ? '1' : '0'); } catch { /* noop */ }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {/* Floating FAB — always visible, becomes "Ask Bosco" pill when collapsed. */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={() => setOpen(true)}
            data-testid="ask-bosco-fab"
            className={cn(
              'fixed bottom-5 right-5 z-40 inline-flex items-center gap-2',
              'h-12 px-4 rounded-full shadow-lg',
              'bg-ae-navy text-white',
              'hover:shadow-glow hover:scale-105 transition-[transform,box-shadow] duration-200',
              'ring-2 ring-ae-cyan/40'
            )}
            title="Open Ask Bosco"
          >
            <Bot className="h-5 w-5" />
            <span className="text-sm font-semibold hidden sm:inline">Ask Bosco</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating panel — appears bottom-right when opened. */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.18 }}
            data-testid="ask-bosco-panel"
            className={cn(
              'fixed bottom-5 right-5 z-40',
              'w-[calc(100vw-2.5rem)] sm:w-[420px] max-w-[420px]',
              'rounded-2xl'
            )}
          >
            {/* Floating top bar with Minimize / Close — sits ABOVE the panel
                so it never overlaps the chat header (which now hosts a 'New' button). */}
            <div className="absolute -top-9 right-0 z-10 flex items-center gap-1">
              <button
                type="button"
                data-testid="ask-bosco-minimize"
                onClick={() => setOpen(false)}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-md text-slate-600 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                title="Minimize"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-testid="ask-bosco-close"
                onClick={() => setOpen(false)}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-md text-slate-600 hover:text-red-600 hover:bg-white dark:hover:bg-slate-800 transition"
                title="Close"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="shadow-2xl rounded-2xl overflow-hidden ring-1 ring-slate-900/10 dark:ring-white/10">
              <TeachingCoPilot access={access} context={context} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
